import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon" className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <span className="text-xl font-display font-bold text-primary">❤️ Flirra</span>
          </Link>
        </div>
      </header>

      <main className="pt-24 pb-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl font-display font-bold text-foreground mb-2">Privatlivspolitik</h1>
          <p className="text-muted-foreground mb-8">Sidst opdateret: Februar 2026</p>

          <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">1. Introduktion</h2>
              <p className="text-muted-foreground leading-relaxed">
                Hos Flirra tager vi dit privatliv alvorligt. Denne privatlivspolitik forklarer, hvordan vi indsamler, 
                bruger, opbevarer og beskytter dine personoplysninger, når du bruger vores dating-platform.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">2. Hvilke oplysninger indsamler vi?</h2>
              <div className="space-y-4 text-muted-foreground">
                <p><strong className="text-foreground">Profiloplysninger:</strong> Navn, email, alder, køn, lokation, interesser, billeder og profilbeskrivelse.</p>
                <p><strong className="text-foreground">Aktivitetsdata:</strong> Swipes, matches, beskeder, og interaktioner med andre brugere.</p>
                <p><strong className="text-foreground">Tekniske data:</strong> IP-adresse, browsertype, enhedsoplysninger og cookies.</p>
                <p><strong className="text-foreground">Betalingsoplysninger:</strong> Betalingshistorik (selve kortoplysninger håndteres af Stripe).</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">3. Hvordan bruger vi dine oplysninger?</h2>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>At levere og forbedre vores dating-tjeneste</li>
                <li>At matche dig med andre brugere baseret på præferencer</li>
                <li>At sende dig notifikationer om matches og beskeder</li>
                <li>At behandle betalinger for premium-funktioner</li>
                <li>At beskytte mod svindel og misbrug</li>
                <li>At overholde lovmæssige forpligtelser</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">4. Deling af oplysninger</h2>
              <p className="text-muted-foreground leading-relaxed">
                Vi sælger aldrig dine personoplysninger. Vi deler kun oplysninger med:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-4">
                <li><strong className="text-foreground">Andre brugere:</strong> Din profil er synlig for andre brugere i discover-funktionen.</li>
                <li><strong className="text-foreground">Tjenesteudbydere:</strong> Stripe (betalinger), hosting-tjenester.</li>
                <li><strong className="text-foreground">Myndigheder:</strong> Kun når lovpåkrævet.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">5. Dine rettigheder (GDPR)</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Som bruger i EU har du følgende rettigheder:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li><strong className="text-foreground">Ret til indsigt:</strong> Du kan anmode om en kopi af dine data.</li>
                <li><strong className="text-foreground">Ret til berigtigelse:</strong> Du kan rette fejl i dine data.</li>
                <li><strong className="text-foreground">Ret til sletning:</strong> Du kan slette din konto og alle data.</li>
                <li><strong className="text-foreground">Ret til dataportabilitet:</strong> Du kan eksportere dine data.</li>
                <li><strong className="text-foreground">Ret til indsigelse:</strong> Du kan modsætte dig visse former for behandling.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">6. Sikkerhed</h2>
              <p className="text-muted-foreground leading-relaxed">
                Vi bruger branchestandard sikkerhedsforanstaltninger til at beskytte dine data, herunder 
                kryptering, sikre servere og regelmæssige sikkerhedsaudits. Ingen metode er dog 100% sikker.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">7. Opbevaring af data</h2>
              <p className="text-muted-foreground leading-relaxed">
                Vi opbevarer dine data så længe du har en aktiv konto. Ved sletning af konto fjernes dine 
                personoplysninger inden for 30 dage, medmindre lovgivning kræver længere opbevaring.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">8. Cookies</h2>
              <p className="text-muted-foreground leading-relaxed">
                Vi bruger cookies til at huske dine præferencer og forbedre din oplevelse. 
                Du kan administrere cookies i dine browserindstillinger.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">9. Kontakt</h2>
              <p className="text-muted-foreground leading-relaxed">
                Har du spørgsmål om vores privatlivspolitik? Kontakt os på:{' '}
                <a href="mailto:privacy@flirra.dk" className="text-primary hover:underline">privacy@flirra.dk</a>
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="max-w-4xl mx-auto px-4 flex flex-wrap gap-6 justify-center text-sm text-muted-foreground">
          <Link to="/privacy" className="hover:text-foreground">Privatlivspolitik</Link>
          <Link to="/terms" className="hover:text-foreground">Brugerbetingelser</Link>
          <Link to="/sales-terms" className="hover:text-foreground">Handelsbetingelser</Link>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
